import { Button } from "@/components/ui/button";
import { ARTIST_AUDIT_KIND_LABEL } from "@/lib/tina/artist-audit";
import type { ArtistAuditKindCounts } from "@/pages/tina/audits/artist/hooks/use-artist-audit-repository";
import { ARTIST_AUDIT_KINDS, type ArtistAuditKind } from "@/types/tina";

interface KindCountStripProps {
  counts: ArtistAuditKindCounts | undefined;
  isLoading: boolean;
  selected: ArtistAuditKind | null;
  onSelect: (kind: ArtistAuditKind | null) => void;
}

/**
 * The shape of the backlog, as five clickable counts.
 *
 * This exists because of how lopsided the real backlog is: the first one is ~1,400
 * findings of which ~1,240 are a single kind, `duplicate_name`. A pager over a mixed
 * 1,400 rows tells a curator nothing about what they are in for and offers no way to do
 * one kind at a time; five counts do both, and each is one click into a queue of a
 * single question.
 *
 * The counts come from five `limit=1` reads of the same route the table pages, so each is
 * the server's own count over the same WHERE — never a tally of the loaded page, which
 * would cap every number at the page size.
 *
 * A count of zero still renders, greyed: "this kind is clear" is information, and a
 * strip whose buttons come and go cannot be clicked reliably.
 */
export function KindCountStrip({
  counts,
  isLoading,
  selected,
  onSelect,
}: KindCountStripProps) {
  const total = counts
    ? ARTIST_AUDIT_KINDS.reduce((sum, kind) => sum + (counts[kind] ?? 0), 0)
    : null;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button
        variant={selected === null ? "default" : "outline"}
        size="sm"
        className="h-7 text-xs"
        onClick={() => onSelect(null)}
      >
        Everything
        <span className="ml-2 font-mono tabular-nums">
          {isLoading || total === null ? "…" : total}
        </span>
      </Button>
      {ARTIST_AUDIT_KINDS.map((kind) => {
        const count = counts?.[kind];
        return (
          <Button
            key={kind}
            variant={selected === kind ? "default" : "outline"}
            size="sm"
            className="h-7 text-xs"
            onClick={() => onSelect(selected === kind ? null : kind)}
          >
            <span className={count === 0 ? "text-muted-foreground" : undefined}>
              {ARTIST_AUDIT_KIND_LABEL[kind]}
            </span>
            <span className="ml-2 font-mono tabular-nums">
              {isLoading || count === undefined ? "…" : count}
            </span>
          </Button>
        );
      })}
    </div>
  );
}
